import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import sanitize from 'sanitize-html';
import { deleteProject, deleteManyProjects } from '@src/apis/project';
import actions from '@src/redux/actions';

// What the dialog is about: null is closed, a project deletes that one, an empty
// object deletes the selection.
const MANY = {};

const useDeleteProjects = ({ selected, onDone }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [target, setTarget] = useState(null);

  const isMany = Boolean(target) && !target.id;

  const notify = (severity, message) =>
    dispatch(actions.noti.push({ severity, message }));

  const confirm = async () => {
    const ids = isMany ? selected.map((project) => project.id) : [target.id];
    // Closed first, so a slow request cannot be confirmed twice.
    setTarget(null);

    try {
      const response = isMany
        ? await deleteManyProjects(ids)
        : await deleteProject(ids[0]);

      if (!response?.status) {
        notify('error', t('deleteProjectError'));
        return;
      }
      notify('success', t('deleteProjectSuccess'));
      onDone(ids);
    } catch (error) {
      notify(
        'error',
        error?.response?.data?.errorMessage || t('deleteProjectError'),
      );
    }
  };

  // The copy holds a <strong>, so it is sanitized before it is rendered as html.
  const description = () =>
    sanitize(
      isMany
        ? t('deleteManyProjectsDescription', { count: selected.length })
        : t('deleteProjectDescription', { projectName: target?.title }),
      { allowedTags: ['strong'] },
    );

  return {
    target,
    askOne: setTarget,
    askMany: () => setTarget(MANY),
    close: () => setTarget(null),
    confirm,
    description,
  };
};

export default useDeleteProjects;
